// ────────────────────────────────────────────────────────────────────────────
// The live order, as the workflow evaluator sees it — and which move is next.
//
// The evaluator in `settings/workflows/lib/evaluate.ts` is already a pure
// function over (subject, requirements), written against sample orders so the
// rules could be felt before they were built. This is the adapter that feeds it
// a REAL order, so the settings screen and the order screen cannot disagree
// about what "no charge lines" means.
//
// FACTS WE CANNOT SEE ARE REPORTED, NOT GUESSED. `supplierPaid` has no
// settlement tracking anywhere yet, so an order carries `false` for it and this
// module names it in `unverifiable` — a rule resting on an unknown fact is
// listed for the operator instead of silently passing or silently blocking.
// Both alternatives are wrong in a way nobody would notice.
// ────────────────────────────────────────────────────────────────────────────
import type {
  Invoice,
  Order,
  OrderChargeCost,
  OrderStatusValue,
  OrderTask,
} from '@my/tms-api';
import {
  chainStates,
  effectiveTransition,
  stateLabel,
  type Workflow,
  type WorkflowTransition,
} from '@/features/settings/workflows/lib/mockWorkflow';
import { evaluate, isBlocked, type SampleOrder, type Unmet } from '@/features/settings/workflows/lib/evaluate';
import { orderStateKey } from '@/features/settings/workflows/lib/invoiceability';
import { NO_POD_COVERAGE, podStateFor, type PodCoverage } from './pod';

/** Requirement kinds nothing in the system can answer yet. */
const UNVERIFIABLE_KINDS = new Set(['supplierPaid']);

/** A task's state in the evaluator's vocabulary. An unknown state reads as open — never as done. */
function taskStateKey(state: OrderTask['state'] | undefined): SampleOrder['tasks'][number]['state'] {
  switch (state) {
    case 'Completed':
      return 'completed';
    case 'Cancelled':
      return 'cancelled';
    case 'InProgress':
      return 'inProgress';
    default:
      return 'pending';
  }
}

/** The Directory payment method, in the evaluator's vocabulary. */
function toPaymentTerms(paymentMethod: string | null | undefined): SampleOrder['paymentTerms'] {
  switch ((paymentMethod ?? '').trim()) {
    case 'Cash':
      return 'cash';
    case 'SelfBill':
      return 'selfBilled';
    case 'Internal':
      return 'internal';
    default:
      // Credit, and anything unrecognised. Treating an incomplete party record
      // as prepaid would block work on a data-quality problem.
      return 'credit';
  }
}

/**
 * The order in the shape the rules read.
 *
 * <p>
 * `invoiced` comes from the CHARGE LINES rather than a flag on the order: a
 * charge carries the `invoiceId` it was pulled into, and that stamp is what the
 * invoice generator writes. Reading it here means the answer stays right for
 * proforma documents, which deliberately stamp nothing.
 * </p>
 */
export function toWorkflowSubject(args: {
  order: Order;
  tasks: OrderTask[];
  charges: OrderChargeCost[];
  /** Invoices raised against this order, for the payment facts. */
  invoices: Invoice[];
  paymentMethod: string | null | undefined;
  /**
   * Reference slots as the FORM currently holds them, unsaved edits included.
   *
   * <p>
   * Judged on the form rather than the stored row because the move PUTs the
   * form: refusing to advance over an empty reference the operator has just
   * typed in — and which the very same click would save — is a button that
   * argues with the screen it sits on. Omitted, the stored columns are used.
   * </p>
   */
  references?: readonly (string | null)[];
  /**
   * POD documents on the order, as coverage over its delivery legs. A scan
   * attached to the order answers the `podSigned` rule exactly as a captured
   * signature does — see `lib/pod.ts` for why that is derived rather than
   * stamped onto the leg.
   */
  podCoverage?: PodCoverage;
}): SampleOrder {
  const { order, tasks, charges } = args;
  const chargeLines = charges.filter((c) => c.side === 'Charge');

  return {
    kind: 'order',
    id: order.id,
    ref: order.orderNumber,
    party: '',
    references: args.references
      ? [...args.references]
      : [
          order.reference1 ?? null,
          order.reference2 ?? null,
          order.reference3 ?? null,
          order.reference4 ?? null,
        ],
    tasks: tasks.map((t) => ({
      type: t.type,
      state: taskStateKey(t.state),
      plannedDate: t.plannedDate ?? null,
      cargoItems: t.cargoItems.length,
      podSigned: podStateFor(t, args.podCoverage ?? NO_POD_COVERAGE) !== null,
    })),
    chargeLines: chargeLines.map((c) => ({ net: c.net })),
    paymentTerms: toPaymentTerms(args.paymentMethod),
    // Any settled invoice counts: a part-invoiced order whose first document is
    // paid has had payment received, which is what the rule asks.
    invoicePaid: args.invoices.some((i) => i.status === 'Paid'),
    invoiced: chargeLines.some((c) => !!c.invoiceId),
    // No supplier-settlement tracking exists. See `unverifiable`.
    supplierPaid: false,
  };
}

export interface MoveAssessment {
  transition: WorkflowTransition;
  /** Where this move leads, as an order status. */
  toStatus: OrderStatusValue;
  /** Human label for the destination, from the org's own state labels. */
  toLabel: string;
  unmet: Unmet[];
  /** True when something at `stop` level is unmet — the move must be refused. */
  blocked: boolean;
  /**
   * Requirement kinds in play that nothing can actually answer yet. Surfaced so
   * an operator is told the check could not be made, rather than being given a
   * pass or a block that is really a shrug.
   */
  unverifiable: string[];
}

/** An order status for a workflow state key, or null when the key is unknown. */
export function statusForStateKey(key: string): OrderStatusValue | null {
  switch (key) {
    case 'draft':
      return 'Draft';
    case 'open':
      return 'Open';
    case 'inProgress':
      return 'InProgress';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return null;
  }
}

/**
 * Assess one transition against a live order.
 *
 * <p>
 * `effectiveTransition` is what gets evaluated, not the raw one: it swaps a
 * cancellation's rules in from the track's single cancel policy and expands a
 * `fromState` threshold into the states it covers. Evaluating the raw
 * transition would silently ignore both.
 * </p>
 */
export function assessMove(
  workflow: Workflow,
  transition: WorkflowTransition,
  subject: SampleOrder,
  referenceLabels: string[],
): MoveAssessment | null {
  const toStatus = statusForStateKey(transition.to);
  if (!toStatus) return null;

  const effective = effectiveTransition(workflow, transition);
  const unmet = evaluate(subject, effective, referenceLabels);

  return {
    transition,
    toStatus,
    toLabel: stateLabel(workflow, transition.to),
    unmet,
    blocked: isBlocked(unmet),
    unverifiable: effective.requirements
      .filter((r) => r.severity !== 'ignore' && UNVERIFIABLE_KINDS.has(r.kind))
      .map((r) => r.kind),
  };
}

/**
 * The moves offered from a status: the next state along the chain, plus
 * cancellation.
 *
 * <p>
 * Forward one step only. A workflow exists to be walked, and letting an operator
 * jump Draft straight to Completed would skip every gate in between — which is
 * the entire point of the gates. Backwards moves are NOT here: they are
 * corrections rather than progress, and they are offered separately by
 * {@link correctionMovesFrom}.
 * </p>
 */
export function movesFrom(workflow: Workflow, status: OrderStatusValue): WorkflowTransition[] {
  const fromKey = orderStateKey(status);
  const chain = chainStates(workflow).map((s) => s.key);
  const at = chain.indexOf(fromKey);
  const nextKey = at >= 0 ? chain[at + 1] : undefined;

  const forward = nextKey
    ? workflow.transitions.find((t) => t.from === fromKey && t.to === nextKey)
    : undefined;

  const abandoning = workflow.states.find((s) => s.abandons === true)?.key;
  const cancel = abandoning
    ? workflow.transitions.find((t) => t.from === fromKey && t.to === abandoning)
    : undefined;

  return [forward, cancel].filter((t): t is WorkflowTransition => !!t);
}

/**
 * The moves that UNDO a mistake rather than advance the order — the states
 * earlier in the chain than this one.
 *
 * <p>
 * Separate from {@link movesFrom} because they are a different act, and the
 * screen treats them as one: the forward move is a button because it is the
 * common case, these live behind the "⋮" and ask before they run.
 * </p>
 *
 * <p>
 * They carry NO requirements, and that is not an oversight. The workflow
 * describes what must be true to move a job FORWARD; nothing describes what must
 * be true to admit a mistake, and inventing rules for it would block the fix
 * rather than the error. What makes a reversal safe to offer is that it is
 * recorded — the audit trail names who moved the order back, when, and from
 * what.
 * </p>
 *
 * <p>
 * A CANCELLED order gets the whole chain: it sits off the chain entirely, so
 * there is no "earlier" to compute, and reinstating it is worth more than making
 * somebody raise a new order that loses this one's number, tasks and charges.
 * </p>
 */
export function correctionMovesFrom(
  workflow: Workflow,
  status: OrderStatusValue,
): MoveAssessment[] {
  const chain = chainStates(workflow);
  const fromKey = orderStateKey(status);
  const at = chain.findIndex((s) => s.key === fromKey);

  // Off the chain (cancelled) → every chain state; on it → only what precedes.
  const targets = at >= 0 ? chain.slice(0, at) : chain;

  return targets.flatMap((state) => {
    const toStatus = statusForStateKey(state.key);
    // A state key the app does not map to a status is one this build does not
    // know — skip it rather than offer a move that cannot be applied.
    if (!toStatus) return [];

    return [
      {
        // A synthetic transition: no configured move exists in this direction,
        // and the shape is only carried so the page can apply it like any other.
        transition: { from: fromKey, to: state.key, setBy: 'operator', requirements: [] },
        toStatus,
        toLabel: state.label,
        unmet: [],
        blocked: false,
        unverifiable: [],
      } satisfies MoveAssessment,
    ];
  });
}
